import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaFilter,
  FaCheck,
  FaTimes,
  FaEye,
  FaMoneyBillAlt,
  FaCalendarAlt
} from 'react-icons/fa';
import CompactActionBar from '../components/CompactActionBar';
import SharedFilter from '../components/SharedFilter';
import { otherIncomeService, type OtherIncome, type OtherIncomeFilters } from '../services/otherIncomeService';
import { projectService } from '../services/projectService';
import ConfirmDialog from '../components/ConfirmDialog';
import { usePageTitle } from '../hooks/usePageTitle';
import '../css/pages/OtherIncome.css';

interface Project {
  id: number;
  name: string;
}

interface IncomeHead {
  id: string;  // CUID
  name: string;
  description?: string;
}

const OtherIncomePage: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const [otherIncomes, setOtherIncomes] = useState<OtherIncome[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [incomeHeads, setIncomeHeads] = useState<IncomeHead[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [incomeToDelete, setIncomeToDelete] = useState<OtherIncome | null>(null);
  const [selectedIncomes, setSelectedIncomes] = useState<string[]>([]);
  const [showBulkApprovalDialog, setShowBulkApprovalDialog] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100);
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterProject, setFilterProject] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterIncomeHead, setFilterIncomeHead] = useState("");
  const [filterNarration, setFilterNarration] = useState("");
  
  const [appliedFilters, setAppliedFilters] = useState({
    project: "",
    dateFrom: "",
    dateTo: "",
    incomeHead: "",
    narration: "",
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [incomeHeadFilter, setIncomeHeadFilter] = useState("");

  const handleApplyFilters = () => {
    setAppliedFilters({
      project: filterProject,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
      incomeHead: filterIncomeHead,
      narration: filterNarration,
    });
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearAllFilters = () => {
    setFilterProject("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterIncomeHead("");
    setFilterNarration("");
    setAppliedFilters({
      project: "",
      dateFrom: "",
      dateTo: "",
      incomeHead: "",
      narration: "",
    });
    setCurrentPage(1);
  };

  // Load projects and income heads only once on mount
  useEffect(() => {
    loadProjects();
    loadIncomeHeads();
  }, []);

  // Load incomes when filters, page, or limit change
  useEffect(() => {
    loadOtherIncomes();
  }, [currentPage, appliedFilters, statusFilter, incomeHeadFilter, limit]);

  const loadOtherIncomes = async () => {
    try {
      setLoading(true);
      const filters: OtherIncomeFilters = {
        search: appliedFilters.narration || '',
        projectId: appliedFilters.project ? parseInt(appliedFilters.project) : undefined,
        incomeHeadId: appliedFilters.incomeHead || undefined,
        status: statusFilter,
        startDate: appliedFilters.dateFrom,
        endDate: appliedFilters.dateTo
      };
      const response = await otherIncomeService.getOtherIncomes({
        ...filters,
        page: currentPage,
        limit: limit
      });
      setOtherIncomes(response.data);
      setTotal(response.pagination.totalRecords);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Error loading other incomes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      const activeProjects = response.filter((p: any) => p.isActive);
      setProjects(activeProjects);
      
      // Auto-select first project
      if (activeProjects.length > 0) {
        const firstProjectId = activeProjects[0].id.toString();
        setFilterProject(firstProjectId);
        setAppliedFilters(prev => ({ ...prev, project: firstProjectId }));
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadIncomeHeads = async () => {
    try {
      const response = await otherIncomeService.getIncomeHeads();
      setIncomeHeads(response);
    } catch (error) {
      console.error('Error loading income heads:', error);
    }
  };

  const handleDelete = (income: OtherIncome) => {
    setIncomeToDelete(income);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (incomeToDelete) {
      try {
        await otherIncomeService.deleteOtherIncome(incomeToDelete.id);
        loadOtherIncomes();
        setShowDeleteDialog(false);
        setIncomeToDelete(null);
      } catch (error) {
        console.error('Error deleting other income:', error);
      }
    }
  };

  const handleApprove = async (income: OtherIncome) => {
    try {
      await otherIncomeService.approveOtherIncome(income.id);
      loadOtherIncomes();
    } catch (error) {
      console.error('Error approving other income:', error);
    }
  };

  const handleReject = async (income: OtherIncome) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      try {
        await otherIncomeService.rejectOtherIncome(income.id, reason);
        loadOtherIncomes();
      } catch (error) {
        console.error('Error rejecting other income:', error);
      }
    }
  };

  const handleEdit = (income: OtherIncome) => {
    navigate(`/ledger/other/edit?id=${income.id}`);
  };

  const handleSelectIncome = (incomeId: string) => {
    setSelectedIncomes(prev => 
      prev.includes(incomeId) 
        ? prev.filter(id => id !== incomeId)
        : [...prev, incomeId]
    );
  };

  const handleSelectAll = () => {
    const pendingIncomes = otherIncomes
      .filter(income => income.status === 'approval_pending')
      .map(income => income.id);
    
    setSelectedIncomes(prev => 
      prev.length === pendingIncomes.length ? [] : pendingIncomes
    );
  };

  const handleBulkApproval = async () => {
    try {
      await otherIncomeService.bulkApproveOtherIncomes(selectedIncomes);
      setSelectedIncomes([]);
      setShowBulkApprovalDialog(false);
      loadOtherIncomes();
    } catch (error) {
      console.error('Error bulk approving incomes:', error);
    }
  };

  return (
    <div className="page-container">
      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <button
            className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>

          <div className="cab-filter-group">
            <span>Project:</span>
            <select
              className="cab-select"
              value={filterProject}
              onChange={(e) => {
                setFilterProject(e.target.value);
                setAppliedFilters((prev) => ({ ...prev, project: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="cab-filter-group">
            <span>Status:</span>
            <select
              className="cab-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="approval_pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="cab-filter-group">
            <span>Income Head:</span>
            <select
              className="cab-select"
              value={incomeHeadFilter}
              onChange={(e) => {
                setIncomeHeadFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Heads</option>
              {incomeHeads.map(head => (
                <option key={head.id} value={head.id}>{head.name}</option>
              ))}
            </select>
          </div>

          <div className="cab-filter-group">
            <span>Show:</span>
            <select
              className="cab-select"
              style={{ minWidth: "80px" }}
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>

          {selectedIncomes.length > 0 && (
            <button 
              className="btn btn-success"
              onClick={() => setShowBulkApprovalDialog(true)}
              style={{ padding: '6px 12px', fontSize: '13px' }}
            >
              <FaCheck /> Approve ({selectedIncomes.length})
            </button>
          )}

          <button 
            className="btn btn-primary"
            onClick={() => navigate('/ledger/other/add')}
            style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: '13px' }}
          >
            <FaPlus /> Add Other Income
          </button>
        </div>
      </CompactActionBar>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{
          background: '#fff',
          padding: '20px',
          marginBottom: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyFilters(); } }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '16px'
          }}>
            {/* Income Head Filter */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Income Head
              </label>
              <select
                value={filterIncomeHead}
                onChange={(e) => setFilterIncomeHead(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              >
                <option value="">All Heads</option>
                {incomeHeads.map(head => (
                  <option key={head.id} value={head.id}>{head.name}</option>
                ))}
              </select>
            </div>

            {/* Narration Search */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Search Narration
              </label>
              <input
                type="text"
                value={filterNarration}
                onChange={(e) => setFilterNarration(e.target.value)}
                placeholder="Search in narration..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Income Date From */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Income Date From
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Income Date To */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Income Date To
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={clearAllFilters}
              style={{
                padding: '8px 16px',
                background: '#fff',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              Clear All
            </button>
            <button
              onClick={handleApplyFilters}
              style={{
                padding: '8px 16px',
                background: '#4f46e5',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center' }}>
          <div style={{fontSize: '16px', color: '#6b7280'}}>Loading other incomes...</div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div style={{ background: '#fff', margin: 0, padding: 0 }}>
          <table className="standard-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedIncomes.length > 0 && selectedIncomes.length === otherIncomes.filter(i => i.status === 'approval_pending').length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Project</th>
                <th>Income Head</th>
                <th>Date</th>
                <th>Narration</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {otherIncomes.map((income, index) => (
                <tr key={income.id}>
                  <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                    {otherIncomes.length - index}
                  </td>
                  <td>
                    {income.status === 'approval_pending' && (
                      <input
                        type="checkbox"
                        checked={selectedIncomes.includes(income.id)}
                        onChange={() => handleSelectIncome(income.id)}
                      />
                    )}
                  </td>
                  <td>
                    <span className="project-name">{income.project.name}</span>
                  </td>
                  <td>
                    <div className="income-head-info">
                      <span className="head-name">{income.incomeHead.name}</span>
                      {income.incomeHead.description && (
                        <span className="head-description">{income.incomeHead.description}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="income-date">{otherIncomeService.formatDate(income.incomeDate)}</span>
                  </td>
                  <td>
                    <span className="narration">{income.narration || '-'}</span>
                  </td>
                  <td>
                    <span className="amount" style={{color: income.amount < 0 ? '#dc2626' : '#059669'}}>{otherIncomeService.formatAmount(income.amount)}</span>
                  </td>
                  <td>
                    <span className={`status ${otherIncomeService.getStatusDisplay(income.status).className}`}>
                      {otherIncomeService.getStatusDisplay(income.status).text}
                    </span>
                  </td>
                  <td>
                    <div className="other-income-actions">
                      {income.status === 'approval_pending' && (
                        <>
                          <button 
                            className="btn-icon approve"
                            onClick={() => handleApprove(income)}
                            title="Approve"
                          >
                            <FaCheck />
                          </button>
                          <button 
                            className="btn-icon reject"
                            onClick={() => handleReject(income)}
                            title="Reject"
                          >
                            <FaTimes />
                          </button>
                          <button 
                            className="btn-icon edit"
                            onClick={() => handleEdit(income)}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            className="btn-icon delete"
                            onClick={() => handleDelete(income)}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </>
                      )}
                      {income.status !== 'approval_pending' && (
                        <button 
                          className="btn-icon view"
                          onClick={() => {/* View details */}}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {otherIncomes.length === 0 && !loading && (
        <div className="empty-state">
          <FaMoneyBillAlt className="empty-icon" />
          <h3>No Other Income Found</h3>
          <p>Start by adding your first other income entry</p>
          <button 
            className="add-other-income-btn"
            onClick={() => navigate('/ledger/other/add')}
          >
            <FaPlus /> Add Other Income
          </button>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="page-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`page-btn ${page === currentPage ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button 
            className="page-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Other Income"
        message={`Are you sure you want to delete this other income entry of ${incomeToDelete ? otherIncomeService.formatAmount(incomeToDelete.amount) : ''}? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setIncomeToDelete(null);
        }}
      />

      {/* Bulk Approval Dialog */}
      <ConfirmDialog
        isOpen={showBulkApprovalDialog}
        title="Bulk Approve Other Income"
        message={`Are you sure you want to approve ${selectedIncomes.length} other income entries?`}
        onConfirm={handleBulkApproval}
        onCancel={() => setShowBulkApprovalDialog(false)}
      />
    </div>
  );
};

export default OtherIncomePage;
